import { createContext, useEffect, useState } from "react";
import { useSurveyStyles } from "./SurveyCustomHooks/useSurveyStyles";
import { useSurveyQuestions } from "./SurveyCustomHooks/useSurveyQuestions";
import { useMediaQuery } from "@mui/material";
import useLocalStorage from "../../../../CustomHooks/useLocalStorage";
import { useSessionStorage } from "../../../../CustomHooks/useSessionStorage";
import { useParams } from "react-router-dom";
import { useSurveyValidations } from "./SurveyCustomHooks/useSurveyValidations";
import { usePersonalInfo } from "./SurveyCustomHooks/usePersonalInfo";

const SurveyContext = createContext();

export const SurveyProvider = ({ children }) => {
    let { userId, customer } = useParams();
    const [themeMode, setThemeMode] = useLocalStorage("themeMode", window.matchMedia("(prefers-color-scheme: dark)").matches ? "light" : "light");
    const darkBackground = '#2b364a';
    const lightBackground = 'whitesmoke';
    const darkColor = '#7375a5';
    const lightColor = '#13c8b5';
    const surveyBackgroundColor = themeMode === "dark" ? darkBackground : lightBackground;
    const [activeStep, setActiveStep] = useState(0);
    const [animation, setAnimation] = useState("slide-in-right");
    const [enableScrollMove, setEnableScrollMove] = useState(true);
    const isSmallScreen = useMediaQuery('(max-width:480px)');
    const [answers, setAnswers] = useLocalStorage(userId, []);
    const [errorMsg, setErrorMsg] = useState("");
    const [ipTimer, setIpTimer] = useSessionStorage("ipTimer", 0);
    const [sending, setSending] = useState(false);

    //#region Personal Info
    const [personalInfo, setPersonalInfo] = useState({
        name: '',
        Id: '',
        correo: '',
        phone: ''
    });
    const [personalInfoErrors, setPersonalInfoErrors] = useState({
        name: '',
        Id: '',
        correo: '',
        phone: ''
    });

    const {
        personalInfoValidations,
        updateUserPersonalInfoMutation
    } = usePersonalInfo();
    //#endregion

    const {
        surveyStyles,
        isLoadingStyles,
        isErrorStyles,
    } = useSurveyStyles();

    const {
        surveyInfo,
        questionsNumber: numberOfQuestions,
        surveyQuestions,
        setSurveyQuestions,
        isLoadingQuestions,
        isErrorQuestions,
        isFetchingQuestions,
        saveAnswersMutation,
        updateUserMutation,
        getIpInfo,
        getLanguageOptions
    } = useSurveyQuestions();

    const [questionsNumber, setQuestionsNumber] = useState(numberOfQuestions || 0);

    useEffect(() => {
        if (!surveyQuestions) return;
        if (surveyInfo?.requiredPersonalInfo || surveyInfo?.personalInfo) {
            setQuestionsNumber(surveyQuestions?.length + 1);
        } else {
            setQuestionsNumber(surveyQuestions?.length);
        }
    }, [surveyQuestions, surveyInfo]);

    //#region dependency questions
    //* Recursively retrieves all dependent question answers that should be deleted.
    //* Esto se hace para que cuando se recargue la pagina se eliminen las respuestas de las preguntas dependientes
    //* porque si las dejo al momento de recargar la pagina se quedan guardadas en el local storage
    //* y no se eliminan las respuestas dependientes que esas solo se abren cuando le doy click a la pregunta que las abre
    //* y como ya estan guardadas en el local storage nunca se abren estas preguntas dependientes

    /**
     * Recursively retrieves all dependent question IDs that should be deleted.
     * 
     * @param {Object} parentQuestion - The parent question object to check for dependencies.
     * @param {string} parentQuestion.itemId - The ID of the parent question.
     * @param {Array} [parentQuestion.depentId] - Array of dependency objects that this question depends on.
     * @param {Array} [itemsToDelete=[]] - Accumulator array of question IDs that should be deleted.
     * @returns {Array} An array of question IDs that should be deleted due to dependencies.
     */
    const getDependentAnswers = (parentQuestion, itemsToDelete = []) => {
        if (!parentQuestion || !parentQuestion.itemId) return itemsToDelete;
        //check if the question is already in the answers
        const existingAnswer = answers.find(answer => answer.survey_item_id === parentQuestion.itemId);
        if (existingAnswer) {
            //check if the question is already in the itemsToDelete array
            const isAlreadyInArray = itemsToDelete.find(item => item === parentQuestion.itemId);
            if (!isAlreadyInArray) {
                itemsToDelete.push(parentQuestion.itemId);
            }
        }
        if (parentQuestion.depentId && parentQuestion.depentId.length > 0) {
            parentQuestion.depentId.forEach(dependency => {
                getDependentAnswers(dependency.dependentQuestion, itemsToDelete);
            });
        }
        return itemsToDelete;
    }

    //* This effect runs when the component mounts and checks if the page was reloaded.
    useEffect(() => {
        const entries = performance.getEntriesByType("navigation");
        if (entries.length > 0 && entries[0].type === "reload" && surveyInfo && answers.length > 0) {
            //get the question that have depentId.length > 0
            const dependentQuestions = surveyInfo?.questions?.filter(question => question.depentId.length > 0);
            //now make a loop to delete the answers of the dependent questions
            const itemsToDeleteArray = [];
            dependentQuestions.forEach(question => {
                const itemsToDelete = getDependentAnswers(question);
                if (Array.isArray(itemsToDelete)) {
                    itemsToDeleteArray.push(...itemsToDelete);
                }
            })
            deleteDependentAnswers(itemsToDeleteArray);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [surveyInfo]);

    /**
     ** Removes answers associated with the survey items marked for deletion.
     * 
     * @param {Array} itemsToDeleteArray - An array of survey item IDs to be deleted
     * @returns {void} - Updates the answers state by filtering out answers that match the deleted survey items
     */
    const deleteDependentAnswers = (itemsToDeleteArray) => {
        if (itemsToDeleteArray.length > 0 && answers.length > 0) {
            const newAnswers = answers.filter(answer => !itemsToDeleteArray.includes(answer.survey_item_id));
            setAnswers(
                prev => newAnswers
            );
        }
    }
    //#endregion

    const {
        handleValidation
    } = useSurveyValidations();

    //#region Save IP info when opening the survey
    useEffect(() => {
        const getCurrentTime = new Date().getTime();
        if (ipTimer > getCurrentTime) {
            return;
        }
        const awaitIp = async () => {
            await getIpInfo();
        };
        awaitIp();
        setIpTimer(getCurrentTime + 600000); // 10 minutes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    //#endregion

    //#region Handle next and back buttons
    const handleNext = (forceNext) => {
        if ((surveyInfo?.personalInfo || surveyInfo?.requiredPersonalInfo) && activeStep === questionsNumber - 1) {
            return;
        }
        //Esto es forzar el siguiente paso cuando se selecciona una respuesta en las tipo radio
        if (typeof forceNext !== "boolean") {
            const validationMsg = handleValidation(surveyQuestions[activeStep], answers, updateAnswers);
            setErrorMsg(validationMsg);
            if (validationMsg !== '') return null;
        } else {
            setErrorMsg("");
        }
        if (!enableScrollMove && typeof forceNext !== "boolean") return;
        if (activeStep >= questionsNumber - 1) return;
        setAnimation("slide-out-left");
        setTimeout(() => {
            setActiveStep(activeStep + 1);
            setAnimation("slide-in-right");
        }, 100);
    };

    const handleBack = () => {
        if (!enableScrollMove) return;
        if (activeStep <= 0) return;
        setAnimation("slide-out-right");
        setTimeout(() => {
            setActiveStep(activeStep - 1);
            setAnimation("slide-in-left");
            setErrorMsg("");
        }, 100);
    };
    //#endregion

    const updateAnswers = (itemId, value, type = "unique") => {
        const existingAnswer = answers.find(answer => answer.survey_item_id === itemId);
        if (existingAnswer) {
            if (type === "multiple") {
                // append new value above the existing one
                setAnswers(prev => [
                    ...prev,
                    {
                        id: userId,
                        survey_item_id: itemId,
                        answer_value: value,
                    },
                ]);
            } else {
                // update the value of an existing answer
                setAnswers(prev =>
                    prev.map(answer =>
                        answer.survey_item_id === itemId
                            ? { ...answer, answer_value: value }
                            : answer
                    )
                );
            }
        } else {
            // add a new answer
            setAnswers(prev => [
                ...prev,
                {
                    id: userId,
                    survey_item_id: itemId,
                    answer_value: value,
                },
            ]);
        }
    }

    return (
        <SurveyContext.Provider value={{
            userId,
            customer,
            surveyBackgroundColor,
            activeStep,
            setActiveStep,
            handleNext,
            handleBack,
            setEnableScrollMove,
            isSmallScreen,
            animation,
            setAnimation,
            questionsNumber,
            surveyStyles,
            isLoadingStyles,
            isErrorStyles,
            surveyInfo,
            surveyQuestions,
            setSurveyQuestions,
            isLoadingQuestions,
            isErrorQuestions,
            isFetchingQuestions,
            themeMode,
            setThemeMode,
            darkBackground,
            darkColor,
            lightBackground,
            lightColor,
            answers,
            setAnswers,
            updateAnswers,
            errorMsg,
            saveAnswersMutation,
            updateUserMutation,
            sending,
            setSending,
            getLanguageOptions,
            personalInfo,
            setPersonalInfo,
            personalInfoErrors,
            setPersonalInfoErrors,
            personalInfoValidations,
            updateUserPersonalInfoMutation,
            getDependentAnswers,
            deleteDependentAnswers
        }}>
            {children}
        </SurveyContext.Provider>
    );
}

export default SurveyContext;
